import { Appointment, AppointmentID } from "./Appointment";
import { Car, CarID } from "./Car";
import { CommunicationAgreement } from "./CheckIn";
import { Customer, CustomerID } from "./Customer";
import { COMMUNICATION_CHANNELS, SMS_GATEWAYS, TwilioMessageSID } from "./CustomerCommunication";
import { COMMUNICATION_RECEIVER_STATUSES } from "./CustomerCommunicationEvent";
import { DealerLocationID } from "./DealerLocation";
import { KeyLockerPIN, KeyLockerRemark } from "./Keylocker";
import { User, UserID } from "./User";

export type KeyLockerCommunicationID = number;

export type KeylockerPinRequestTypeID = number;

export type KeyLockerCommunicationEventID = number;

export type KeyLockerCommunicationReceiverID = number;

export type KeyLockerAgreedInterventionID = number;

export enum KEYLOCKER_COMMUNICATION_STATUSES {
  DropPINSent = 1,
  CheckInOpened = 2,
  CheckInAnswered = 3,
  PickUpPINSent = 4
}

export enum KEYLOCKER_COMMUNICATION_EVENT_TYPE {
  DropPINSent = 1,
  CheckInOpened = 2,
  CheckInAnswered = 3,
  PickUpPINSent = 4
}

export enum KEYLOCKER_PIN_REQUEST_TYPES {
  PhoneNumber = 1,
  RegNumber = 2,
  TwoFADisabled = 3,
  OnlineCheckIn = 4
}

export interface KeyLockerCommunication {
  created_on: string;
  updated_on: string;
  id: KeyLockerCommunicationID;
  status: KEYLOCKER_COMMUNICATION_STATUSES; // Current status of the keylocker communication
  pin: KeyLockerPIN | null; // PIN of a KeyLocker the Customer can access
  pin_expire_on: string | null; // PIN Expiration date
  customer_id: CustomerID | null; // Customer who this KeyLockerCommunication is for
  appointment_id: AppointmentID; // Appointment from which this KeyLockerCommunication originated from
  car_id: CarID; // Car from which the Appointment is for
  dealer_location_id: DealerLocationID; // DealerLocation from which the Appointment is from
  customer?: Customer;
  appointment?: Appointment;
  car?: Car;
  events?: KeyLockerCommunicationEvent[];
  agreements?: CommunicationAgreement[]; // CommunicationAgreements that have been agreed
  agreed_interventions?: KeyLockerAgreedIntervention[]; // Slice of agreed interventions
  remark?: KeyLockerRemark; // Customer provided a remark on the communication
}

export interface KeyLockerCommunicationEvent {
  created_on: string;
  updated_on: string;
  id: KeyLockerCommunicationEventID;
  type: KEYLOCKER_COMMUNICATION_EVENT_TYPE; // Type of the KeyLockerCommunicationEvent
  correct_phone: string; // Correct phone number of the customer
  customer_name: string; // Name of the customer
  pin_request_type: KEYLOCKER_PIN_REQUEST_TYPES | null; // Type of customer identifier used for requesting PIN
  user_id: UserID | null; // Identifier of the user
  keylocker_name: string | null; // Name of the Keylocker that triggered this event
  keylocker_box_number: number | null; // KeyLocker Box Number used to trigger this event
  keylocker_communication_id: KeyLockerCommunicationID; // Identifier of the KeyLocker Communication
  receivers?: KeyLockerCommunicationReceiver[];
  keylocker_agreed_interventions?: KeyLockerAgreedIntervention[];
  user?: User;
  appointment_id?: AppointmentID;
}

export interface KeyLockerCommunicationReceiver {
  created_on: string;
  updated_on: string;
  id: KeyLockerCommunicationReceiverID;
  communication_channel_id: COMMUNICATION_CHANNELS; // Type of communication channel used
  destination: string; // Recipient identifier for the channel used
  status: COMMUNICATION_RECEIVER_STATUSES; // Status of the communication receiver
  reason: string; // Reason in case of any failure
  sms_gateway_id: SMS_GATEWAYS | null; // Type of gateway used in case of SMS
  twilio_message_sid: TwilioMessageSID; // Identifier of the SMS in Twilio
  mailgun_message_id: string; // Mailgun message identifier for identifying the email
  keylocker_communication_id: KeyLockerCommunicationEventID; // KeyLocker Communication ID
}

export interface KeyLockerAgreedIntervention {
  created_on: string;
  id: KeyLockerAgreedInterventionID;
  title: string; // Title of the agreed intervention
  description: string; // Description of the agreed intervention
  price: number; // Price of the agreed intervention
  vat: number | null; // VAT of the agreed intervention
  keylocker_communication_id: KeyLockerCommunicationID; // KeyLocker Communication ID
}
